<script setup lang="ts">
import { computed, ref } from 'vue'
import { getReturnsAddress } from '~~/app/brands'

const brand = useBrand()
const { sellingEnabled } = useCatalogOnly()
const { externalUrl, withdrawalUrl } = useReturns()
const isYoulook = computed(() => brand.id === 'youlook')
const title = computed(() => (isYoulook.value ? 'Najczęściej zadawane pytania' : 'FAQ'))

interface FaqItem {
  question: string
  answer: string
}

/**
 * Pytania sklepu KATALOGOWEGO — innych niż w sklepie, który sprzedaje.
 *
 * Lista poniżej pisana jest pod zakupy: rozmiarówka, zwroty, dostawa, płatności,
 * pranie ubrań. W sklepie bez koszyka, sprzedającym gadżety z nadrukiem, każde
 * z tych pytań jest fałszywe — obiecuje ścieżkę, której nie ma, i mówi o innym
 * asortymencie. Klient B2B pyta o zupełnie inne rzeczy: nakład, znakowanie,
 * termin i projekt.
 */
const catalogItems: FaqItem[] = [
  {
    question: 'Jak zamówić gadżety z logo?',
    answer:
      'Przeglądasz katalog i dodajesz interesujące produkty do listy zapytania — możesz zebrać kilkanaście pozycji naraz. Przy wysyłce podajesz nakład dla każdej z nich, a my odsyłamy wycenę. Zamówienie potwierdzasz dopiero po akceptacji ceny i projektu znakowania.',
  },
  {
    question: 'Od jakiego nakładu realizujecie zamówienia?',
    answer:
      'Minimalny nakład zależy od produktu i techniki znakowania — przy nadruku bywa niższy niż przy grawerze czy hafcie. Podaj interesującą Cię ilość w zapytaniu, a w odpowiedzi otrzymasz cenę za sztukę dla tego nakładu oraz informację, czy da się go obniżyć.',
  },
  {
    question: 'Jakie techniki znakowania są dostępne?',
    answer:
      'Najczęściej stosujemy nadruk (tampodruk, sitodruk, nadruk cyfrowy), grawer laserowy, tłoczenie i haft. Wybór zależy od materiału i kształtu produktu — jeśli nie masz pewności, którą technikę wybrać, napisz to w zapytaniu, a doradzimy.',
  },
  {
    question: 'Ile trwa przygotowanie wyceny?',
    answer:
      'Wycenę przygotowujemy zwykle w ciągu jednego dnia roboczego. Zawiera cenę za sztukę dla podanego nakładu, koszt znakowania i orientacyjny termin realizacji.',
  },
  {
    question: 'Czy przygotujecie projekt znakowania?',
    answer:
      'Tak. Wystarczy przesłać logo w pliku wektorowym lub w dobrej jakości — do zapytania możesz dołączyć załącznik. Odeślemy wizualizację produktu ze znakowaniem do akceptacji przed rozpoczęciem produkcji.',
  },
  {
    question: 'Czy mogę zobaczyć próbkę przed produkcją?',
    answer:
      'Przy większych nakładach przygotowujemy próbkę lub wizualizację do zatwierdzenia. Zaznacz taką potrzebę w zapytaniu — uwzględnimy ją w wycenie i w terminie realizacji.',
  },
]

/**
 * Odpowiedź o zwrotach zależy od tego, czy sklep ma serwis zwrotów online.
 * Marka bez konta „Wygodne Zwroty" (discanto) przyjmuje oświadczenie mailem
 * i paczkę na adres zwrotu — opis ścieżki przez konto byłby tam nieprawdziwy.
 * Sklepy z serwisem (lavmag, youlook) dostają tekst bez zmian.
 */
const returnsAnswer = computed(() => {
  if (withdrawalUrl || externalUrl) {
    return 'Masz 14 dni od otrzymania przesyłki na odstąpienie od umowy bez podania przyczyny. Zwrot zgłosisz wygodnie z poziomu swojego konta w zakładce „Moje zamówienia" — wybierz zamówienie i produkty, które chcesz zwrócić. Zwracany towar powinien być nieużywany i z metkami.'
  }
  const a = getReturnsAddress(brand)
  return `Masz 14 dni od otrzymania przesyłki na odstąpienie od umowy bez podania przyczyny. Napisz na ${brand.footer.email}, że odstępujesz od umowy (podaj numer zamówienia), i odeślij produkty na adres: ${a.name}, ${a.street}, ${a.postalCode} ${a.city}. Zwracany towar powinien być nieużywany i z metkami.`
})

const sellingItems = computed<FaqItem[]>(() => [
  {
    question: 'Jak mogę sprawdzić, czy wybrany rozmiar będzie na mnie pasował?',
    answer:
      'Przy każdym produkcie znajdziesz tabelę rozmiarów z dokładnymi wymiarami. Zmierz obwód klatki piersiowej, talii i bioder, a następnie porównaj wyniki z tabelą — to najpewniejszy sposób na trafiony rozmiar. Jeśli wahasz się między dwoma rozmiarami, napisz do nas, a pomożemy dobrać właściwy.',
  },
  {
    question: 'Ile mam czasu na zwrot towaru i jak go dokonać?',
    answer: returnsAnswer.value,
  },
  {
    question: 'Jakie są koszty i metody dostawy?',
    answer:
      `Przesyłki realizujemy kurierem oraz do paczkomatów InPost. Przy zamówieniach od ${brand.freeShippingThreshold} zł dostawa jest darmowa — poniżej tej kwoty koszt zależy od wybranej metody i widoczny jest w koszyku przed złożeniem zamówienia.`,
  },
  {
    question: 'Czy mogę wymienić produkt na inny rozmiar lub kolor?',
    answer:
      'Nie prowadzimy bezpośredniej wymiany, ale jest to bardzo proste: wystarczy zwrócić otrzymany produkt i złożyć nowe zamówienie na właściwy rozmiar lub kolor. Dzięki temu masz pewność, że wybrany wariant jest dostępny.',
  },
  {
    question: 'Jakie formy płatności są dostępne w sklepie?',
    answer:
      'Udostępniamy szybkie płatności online Przelewy24 (BLIK, przelewy ekspresowe, karta) oraz płatność za pobraniem przy odbiorze. Metodę płatności wybierzesz na ostatnim kroku składania zamówienia.',
  },
  {
    question: 'Jak dbać o zakupione ubrania, aby służyły jak najdłużej?',
    answer:
      'Zawsze kieruj się symbolami na metce produktu. W większości przypadków warto prać w niskiej temperaturze, na lewej stronie i unikać suszarki bębnowej — to chroni kolory i strukturę materiału. Delikatne tkaniny najlepiej prać ręcznie lub w woreczku ochronnym.',
  },
  {
    question: 'Czy oferujecie kody rabatowe?',
    answer:
      'Tak! Najłatwiej zdobyć rabat zapisując się do naszego newslettera — informujemy w nim o promocjach i kodach. Posiadany kod wpiszesz w koszyku w polu „Kod rabatowy", a zniżka naliczy się automatycznie.',
  },
])

const items = computed<FaqItem[]>(() => (sellingEnabled ? sellingItems.value : catalogItems))

const openIndex = ref<number | null>(null)

function toggle(index: number) {
  openIndex.value = openIndex.value === index ? null : index
}
</script>

<template>
  <section
    class="faq"
    :class="{ 'faq--youlook': isYoulook }"
  >
    <div class="faq__inner container">
      <h2 class="faq__title">{{ title }}</h2>
      <div class="faq__list">
        <div
          v-for="(item, index) in items"
          :key="index"
          class="faq__item"
        >
          <button
            type="button"
            class="faq__header"
            :aria-expanded="openIndex === index"
            @click="toggle(index)"
          >
            <span class="faq__question">{{ item.question }}</span>
            <svg
              class="faq__icon"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
              aria-hidden="true"
            >
              <path d="M3 10H17" />
              <path v-if="openIndex !== index" d="M10 3V17" />
            </svg>
          </button>
          <div
            v-show="openIndex === index"
            class="faq__body"
          >
            <p>{{ item.answer }}</p>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
.faq {
  background: var(--color-white);
  padding: 48px 0;
}

.faq__inner {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.faq__title {
  margin: 0 0 32px;
  font-size: 128px;
  font-weight: var(--font-weight-light);
  color: var(--color-black);
  line-height: 0.85;
  letter-spacing: -0.02em;
  text-align: center;
  text-transform: uppercase;
}

/* youlook: h2 to pełne zdanie, nie 3-literowe „FAQ" — czytelny rozmiar bez uppercase */
.faq--youlook .faq__title {
  font-size: 44px;
  font-weight: var(--font-weight-normal);
  line-height: 1.15;
  letter-spacing: normal;
  text-transform: none;
}

.faq__list {
  width: 100%;
  max-width: 820px;
  display: flex;
  flex-direction: column;
  border-top: 1px solid var(--color-border);
}

.faq__item {
  border-bottom: 1px solid var(--color-border);
}

.faq__header {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 20px 0;
  background: none;
  border: none;
  font-family: var(--font-body);
  font-size: 1rem;
  font-weight: var(--font-weight-normal);
  color: var(--color-black);
  cursor: pointer;
  text-align: left;
}

.faq__question {
  flex: 1;
}

.faq__icon {
  flex-shrink: 0;
  color: var(--color-black);
  opacity: 0.75;
}

.faq__body {
  padding: 0 0 20px;
  font-size: 0.9375rem;
  font-weight: var(--font-weight-light);
  color: var(--color-black);
  line-height: 1.6;
}

.faq__body p {
  margin: 0;
  max-width: 720px;
}

@media (max-width: 960px) {
  .faq {
    padding: 20px 0;
  }

  .faq__title {
    font-size: 72px;
  }

  .faq--youlook .faq__title {
    font-size: 34px;
  }
}

@media (max-width: 560px) {
  .faq__title {
    font-size: 48px;
  }

  .faq--youlook .faq__title {
    font-size: 26px;
  }

  .faq__header {
    font-size: 0.9375rem;
  }
}
</style>
